import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  memo,
} from "react";
import {
  FormProvider,
  useForm,
  Controller,
  useWatch,
  useFormContext,
} from "react-hook-form";
import { useQuery } from "react-query";
import axios from "../../config/axiosInstance";
import { ADMIN, AUDIT } from "../../config/apiConfig";
import {
  Alert,
  Badge,
  Button,
  TextInput,
  Tabs,
  Select,
  Tooltip, // <- novo
} from "flowbite-react";
import { HiInformationCircle } from "react-icons/hi";
import { AiOutlineLoading } from "react-icons/ai";
import {
  MdAddCircleOutline,
  MdClear,
  MdClearAll,
  MdDoneAll,
  MdFilterList,
  MdHistory,
  MdOutlineTableView,
  MdStarBorder,
} from "react-icons/md";
import DrawerLoading from "../DrawerLoading";
import CurrencyInput from "../inputs/CurrencyInput";
import SelectInput from "../inputs/SelectInput";
import CustomSwitch from "../inputs/CustomSwitch";
import AuditHistoryModal from "../modals/AuditHistoryModal";
import { formatToReal } from "../../utils/utils";
import { getDocType } from "../../utils/masks";
import { useVirtualizer } from "@tanstack/react-virtual";

/* ---------- pequenos átomos (estilo refinado) ---------- */

const renderStatus = (status) => {
  switch (status) {
    case 0:
      return (
        <div
          className="text-[10px] bg-orange-50 px-1 font-semibold text-orange-600"
          color="success"
        >
          Em manutenção
        </div>
      );
    case 1:
      return (
        <div
          className="text-[10px] bg-green-50 px-1 font-semibold text-green-600"
          color="success"
        >
          Ativo
        </div>
      );
    case -1:
      return (
        <div
          className="text-[10px] bg-red-50 px-1 font-semibold text-red-600"
          color="success"
        >
          Indisponível
        </div>
      );
  }
};

const Segmented = ({ value, onChange, stats }) => {
  const base =
    "px-3 py-1.5 text-sm rounded-full transition border flex items-center gap-2";
  const active = "bg-emerald-600 text-white border-emerald-600 shadow-sm";
  const inactive = "bg-white text-gray-700 border-gray-300 hover:bg-gray-50";

  return (
    <div
      role="tablist"
      aria-label="Tipo de consulta"
      className="inline-flex items-center gap-1 p-1 bg-gray-100 rounded-full border border-gray-200"
    >
      <button
        type="button"
        role="tab"
        aria-selected={value === "main"}
        onClick={() => onChange("main")}
        className={`${base} ${value === "main" ? active : inactive}`}
      >
        <MdStarBorder className="opacity-90" />
        <span>Principais</span>
        <span
          className={`text-[11px] ${
            value === "main" ? "text-white/90" : "text-gray-500"
          }`}
        >
          {stats.main.visible}/{stats.main.total}
        </span>
      </button>

      <button
        type="button"
        role="tab"
        aria-selected={value === "add"}
        onClick={() => onChange("add")}
        className={`${base} ${value === "add" ? active : inactive}`}
      >
        <MdAddCircleOutline className="opacity-90" />
        <span>Adicionais</span>
        <span
          className={`text-[11px] ${
            value === "add" ? "text-white/90" : "text-gray-500"
          }`}
        >
          {stats.add.visible}/{stats.add.total}
        </span>
      </button>
    </div>
  );
};

const Card = ({ children, className = "" }) => (
  <div
    className={[
      "rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden",
      "shadow-sm",
      className,
    ].join(" ")}
  >
    {children}
  </div>
);

const CardHeader = ({ title, right }) => (
  <div className="flex items-center justify-between px-4 md:px-5 py-3 border-b border-gray-200/70 dark:border-gray-700/70 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 rounded-t-2xl">
    <h3 className="text-sm md:text-base font-semibold text-gray-800 dark:text-gray-100">
      {title}
    </h3>
    <div className="flex items-center gap-2 flex-wrap justify-end">{right}</div>
  </div>
);

const CardBody = ({ children, className = "" }) => (
  <div className={["px-4 md:px-5 py-4", className].join(" ")}>{children}</div>
);

const StatChip = ({ label, value }) => (
  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 max-w-full">
    <span className="text-[11px] text-gray-500">{label}</span>
    <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate max-w-[140px] md:max-w-[220px]">
      {value}
    </span>
  </div>
);

function useDebounce(value, delay = 500) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/* ---------- Row super leve (memo) com layout melhor ---------- */
const QueryRow = memo(function QueryRow({
  name,
  basePrice,
  fieldPathActive,
  fieldPathPrice,
  cqpId,
  onOpenHistory,
  onValidityChange,
  status,
}) {
  const { control, clearErrors, setError } = useFormContext();
  const isActive = !!useWatch({ control, name: fieldPathActive });
  const typedPrice = Number(useWatch({ control, name: fieldPathPrice }) ?? 0);
  const debouncedPrice = useDebounce(typedPrice, 350);
  const base = Number(basePrice || 0);
  const isInvalid = isActive && debouncedPrice < base;

  useEffect(() => {
    onValidityChange?.(isInvalid, fieldPathPrice);
    if (!isActive) {
      clearErrors(fieldPathPrice);
      return;
    }
    if (isInvalid) {
      setError(fieldPathPrice, {
        type: "min",
        message: `Preço não pode ser menor que o custo (${formatToReal(base)})`,
      });
    } else {
      clearErrors(fieldPathPrice);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInvalid, isActive, fieldPathPrice, base]);

  const marginPct = base > 0 ? ((debouncedPrice - base) / base) * 100 : null;
  const marginTone =
    !isActive || marginPct === null
      ? "text-gray-500 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"
      : marginPct < 10
        ? "text-orange-700 border-orange-200 dark:border-orange-700/60 bg-orange-50"
        : "text-emerald-700 border-emerald-200 dark:border-emerald-700/60 bg-emerald-50";

  return (
    <div className="px-2 sm:px-3 py-1 border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50/60 dark:hover:bg-gray-800/40 transition-colors">
      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto] gap-3 items-center py-2">
        {/* Esquerda: switch + título */}
        <Controller
          control={control}
          name={fieldPathActive}
          render={({ field }) => (
            <div className="min-w-0">
              <CustomSwitch
                id={`switch-${fieldPathActive}`}
                checked={!!field.value}
                onChange={field.onChange}
                label={
                  <div className="flex gap-2 items-center">
                    <span className="truncate font-medium text-gray-900 dark:text-gray-100">
                      {name}
                    </span>
                    {renderStatus(status)}
                  </div>
                }
                subtitle={
                  <span className="text-xs text-gray-500">
                    Custo: {formatToReal(base)}
                  </span>
                }
                size="md"
                color="emerald"
              />
            </div>
          )}
        />

        {/* Direita: preço + margem + histórico */}
        <div className="flex flex-wrap items-center justify-start md:justify-end gap-2">
          <div className="w-full sm:w-44">
            <CurrencyInput
              name={fieldPathPrice}
              label="Preço"
              control={control}
              disabled={!isActive}
              className={[
                "w-full",
                isInvalid
                  ? "ring-1 ring-red-500 border-red-500 rounded-md"
                  : "",
              ].join(" ")}
            />
          </div>

          <div
            className={[
              "shrink-0 px-3 py-1.5 rounded-full text-[11px] font-semibold border",
              "min-w-[84px] h-9 flex items-center justify-center",
              marginTone,
            ].join(" ")}
          >
            {marginPct === null ? "—%" : `Margem ${marginPct.toFixed(2)}%`}
          </div>

          <span className="shrink-0">
            <Button
              color="light"
              size="xs"
              title="Histórico desta permissão"
              className="shrink-0"
              disabled={!cqpId}
              onClick={onOpenHistory}
            >
              <MdHistory fontSize={16} />
            </Button>
          </span>
        </div>
      </div>

      <div className="mt-1">
        {isInvalid ? (
          <div className="text-[11px] text-red-600">
            Preço não pode ser menor que o custo ({formatToReal(base)}).
          </div>
        ) : !isActive ? null : null}
      </div>
    </div>
  );
});

/* ---------- Lista Virtualizada com visual caprichado ---------- */
function VirtualQueryList({
  entries,
  getCqpId,
  onOpenHistory,
  onValidityChange,
  containerHeight = 520,
}) {
  const parentRef = useRef(null);

  const rowVirtualizer = useVirtualizer({
    count: entries.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 76,
    overscan: 10,
  });

  const items = rowVirtualizer.getVirtualItems();

  return (
    <div
      ref={parentRef}
      className="overflow-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
      style={{ height: containerHeight }}
    >
      {/* Top shadow */}
      <div
        style={{
          height: rowVirtualizer.getTotalSize(),
          width: "100%",
          position: "relative",
        }}
      >
        {items.map((vi) => {
          const e = entries[vi.index];
          const cqpId = getCqpId(e);
          return (
            <div
              key={e.id}
              data-index={vi.index}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${vi.start}px)`,
              }}
            >
              <QueryRow
                name={e.name}
                basePrice={e.basePrice}
                fieldPathActive={e.fieldPathActive}
                fieldPathPrice={e.fieldPathPrice}
                cqpId={cqpId}
                onOpenHistory={() => onOpenHistory(cqpId, e.name)}
                onValidityChange={onValidityChange}
                status={e.status}
              />
            </div>
          );
        })}
      </div>
      {/* Bottom shadow */}
    </div>
  );
}

/* ---------- Principal com layout refinado ---------- */
const CompanyQueriesFormV2 = ({
  company,
  categories,
  fetchCompanies,
  onClose,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setErrorState] = useState(null);
  const [saved, setSaved] = useState(false);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyCtx, setHistoryCtx] = useState({ cqpId: null, title: "" });

  const [detachedFromTable, setDetachedFromTable] = useState(false);
  const [activeCategoryIdx, setActiveCategoryIdx] = useState(0);
  const [subTab, setSubTab] = useState("main");
  const [filter, setFilter] = useState("");
  const [isMobile, setIsMobile] = useState(false);

  const invalidSetRef = useRef(new Set());
  const applyingRef = useRef(false);
  const snapshotRef = useRef({});

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener("change", update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", update);
      else mq.removeListener(update);
    };
  }, []);

  const methods = useForm({
    defaultValues: {
      company: {
        hasSurvey: false,
        hasCV: false,
        priceSurvey: 10,
        priceCV: 10,
        vcm: "",
      },
      tableId: "",
      categories: [],
    },
  });
  const { register, reset, setValue, handleSubmit, watch } = methods;
  const tableId = watch("tableId");
  const watchedCategories = watch("categories", []);

  const isCompany = useMemo(() => {
    const t = company?.typeCustomer || company?.TypeCustomer || null;
    if (t) return String(t).toUpperCase() === "CNPJ";
    const doc =
      company?.document || company?.cnpj || company?.cpfCnpj || company?.taxId;
    return Boolean(doc) && getDocType(doc) === "CNPJ";
  }, [company]);

  useEffect(() => {
    // Se este cliente não for empresa, desativa e limpa campos de CV
    if (!isCompany) {
      setValue("company.hasCV", false, { shouldDirty: true });
      setValue("company.priceCV", 0, { shouldDirty: true });
    }
  }, [isCompany, setValue]);

  /* ---------- data ---------- */
  const { isLoading: loadingCQ, data: companyQueries } = useQuery(
    ["companyQueries", company?.id],
    async () =>
      (await axios.get(`${ADMIN.GET_COMPANY_QUERIES}/${company?.id}`)).data
        .data,
    { enabled: !!company?.id, staleTime: 60_000, keepPreviousData: true },
  );

  const { isLoading: loadingTables, data: tables } = useQuery(
    ["tables"],
    async () =>
      (await axios.get(`${ADMIN.GET_TABLES}?itemsPerPage=-1`)).data.data.data,
    { enabled: !!company, staleTime: 120_000, keepPreviousData: true },
  );

  const { isFetching: fetchingTableQueries, data: tableQueriesPkg } = useQuery(
    ["tableQueries", tableId],
    async () => {
      if (!tableId) return null;
      const data = (await axios.get(`${ADMIN.GET_TABLE_QUERIES}/${tableId}`))
        .data.data;
      return { tableId, data };
    },
    { enabled: !!tableId, staleTime: 60_000, keepPreviousData: true },
  );

  /* ---------- bootstrapping ---------- */
  useEffect(() => {
    if (!company || !companyQueries || !categories) return;

    const defaults = {
      tableId: company?.tableId ? String(company.tableId) : "",
      company: {
        hasSurvey: !!company.hasSurvey,
        hasCV: !!company.hasCV,
        priceSurvey: company.priceSurvey ?? 10,
        priceCV: company.priceCV ?? 10,
        vcm: company.vcm || "",
      },
      categories: categories.map((cat) => ({
        queries: cat.Queries.map((q) => {
          const matched = companyQueries.find((cq) => cq.QueryId === q.id);
          return {
            QueryId: q.id,
            isActive: !!matched,
            price: matched?.price ?? q.price,
            cqpId: matched?.cqpId || null,
          };
        }),
      })),
    };

    reset(defaults);

    const snap = {};
    defaults.categories.forEach((c) =>
      c.queries.forEach((q) => {
        snap[q.QueryId] = {
          cqpId: q.cqpId,
          isActive: q.isActive,
          price: Number(q.price ?? 0),
        };
      }),
    );
    snapshotRef.current = snap;

    applyingRef.current = true;
    setValue("company.hasSurvey", !!company?.hasSurvey, { shouldDirty: false });
    setValue("company.hasCV", !!company?.hasCV, { shouldDirty: false });
    setValue("company.priceSurvey", company?.priceSurvey ?? 10, {
      shouldDirty: false,
    });
    setValue("company.priceCV", company?.priceCV ?? 10, { shouldDirty: false });
    setValue("tableId", company?.tableId ? String(company.tableId) : "", {
      shouldDirty: false,
    });
    setDetachedFromTable(false);
    queueMicrotask(() => (applyingRef.current = false));
  }, [company, companyQueries, categories, reset, setValue]);

  useEffect(() => {
    if (!tables || !tableId) return;
    const sel = tables.find((t) => String(t.id) === String(tableId));
    if (!sel) return;

    applyingRef.current = true;
    setValue("company.vcm", sel.vcm || "", { shouldDirty: false });
    setValue("company.hasSurvey", !!sel.hasSurvey, { shouldDirty: false });
    setValue("company.hasCV", !!sel.hasCV, { shouldDirty: false });
    setValue("company.priceSurvey", sel.priceSurvey ?? 10, {
      shouldDirty: false,
    });
    setValue("company.priceCV", sel.priceCV ?? 10, { shouldDirty: false });
    queueMicrotask(() => (applyingRef.current = false));
  }, [tableId, tables, setValue]);

  useEffect(() => {
    if (
      !tableId ||
      !tableQueriesPkg?.data ||
      tableQueriesPkg.tableId !== tableId ||
      !categories
    )
      return;

    applyingRef.current = true;
    const tqs = tableQueriesPkg.data;
    categories.forEach((category, idxCat) => {
      category.Queries.forEach((q, idxQ) => {
        const matched = tqs.find((tq) => tq.QueryId === q.id);
        setValue(`categories[${idxCat}].queries[${idxQ}].isActive`, !!matched, {
          shouldDirty: false,
        });
        setValue(
          `categories[${idxCat}].queries[${idxQ}].price`,
          matched?.price ?? q.price,
          { shouldDirty: false },
        );
      });
    });
    queueMicrotask(() => (applyingRef.current = false));
  }, [tableId, tableQueriesPkg, categories, setValue]);

  useEffect(() => {
    const sub = watch((_, { name }) => {
      if (!watch("tableId") || applyingRef.current) return;
      const isCompanyField =
        name === "company.priceSurvey" ||
        name === "company.priceCV" ||
        name === "company.hasSurvey" ||
        name === "company.hasCV";
      const isQueryPrice = /^categories\[\d+\]\.queries\[\d+\]\.price$/.test(
        name || "",
      );
      const isQueryActive =
        /^categories\[\d+\]\.queries\[\d+\]\.isActive$/.test(name || "");
      if (isCompanyField || isQueryPrice || isQueryActive) {
        setValue("tableId", "", { shouldDirty: false });
        setDetachedFromTable(true);
      }
    });
    return () => sub.unsubscribe();
  }, [watch, setValue]);

  /* ---------- helpers ---------- */
  const handleValidityChange = useCallback((isInvalid, path) => {
    const set = invalidSetRef.current;
    if (isInvalid) set.add(path);
    else set.delete(path);
  }, []);
  const hasInvalidPrice = invalidSetRef.current.size > 0;

  const totalActive = useMemo(() => {
    try {
      return (watchedCategories || []).reduce((sum, cat) => {
        const qs = cat?.queries || [];
        return sum + qs.reduce((acc, q) => acc + (q?.isActive ? 1 : 0), 0);
      }, 0);
    } catch {
      return 0;
    }
  }, [watchedCategories]);

  const openHistory = useCallback((cqpId, title) => {
    if (!cqpId) return;
    setHistoryCtx({ cqpId, title });
    setHistoryOpen(true);
  }, []);

  /* ---------- derivação estável ---------- */
  const derived = useMemo(() => {
    if (!categories) return [];
    return categories.map((cat, idxCat) => {
      const entries = (cat?.Queries || []).map((q, idxQ) => ({
        id: q.id,
        name: q.name || "",
        status: q.status || 0,
        isAdditional: !!q.isAdditional,
        basePrice: q.price,
        fieldPathPrice: `categories[${idxCat}].queries[${idxQ}].price`,
        fieldPathActive: `categories[${idxCat}].queries[${idxQ}].isActive`,
        fieldPathCqp: `categories[${idxCat}].queries[${idxQ}].cqpId`,
        idxCat,
        idxQ,
      }));
      return { idxCat, name: cat.name, entries };
    });
  }, [categories]);

  const tabStats = useMemo(() => {
    const cat = derived?.[activeCategoryIdx];
    if (!cat) {
      return {
        main: { total: 0, visible: 0 },
        add: { total: 0, visible: 0 },
      };
    }
    const low = (filter || "").toLowerCase();

    const mainAll = cat.entries.filter((e) => !e.isAdditional);
    const addAll = cat.entries.filter((e) => e.isAdditional);

    const mainVisible = mainAll.filter((e) =>
      low ? e.name.toLowerCase().includes(low) : true,
    );
    const addVisible = addAll.filter((e) =>
      low ? e.name.toLowerCase().includes(low) : true,
    );

    return {
      main: { total: mainAll.length, visible: mainVisible.length },
      add: { total: addAll.length, visible: addVisible.length },
    };
  }, [derived, activeCategoryIdx, filter]);

  const activeCategory = derived[activeCategoryIdx];

  const entriesForSubTab = useMemo(() => {
    if (!activeCategory) return [];
    const low = filter.toLowerCase();
    return activeCategory.entries.filter((e) => {
      const okKind = subTab === "main" ? !e.isAdditional : e.isAdditional;
      if (!okKind) return false;
      return low ? e.name.toLowerCase().includes(low) : true;
    });
  }, [activeCategory, subTab, filter]);

  const getCqpId = useCallback(
    (e) => watch(e.fieldPathCqp),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [watch, activeCategoryIdx, subTab],
  );

  /* ---------- submit ---------- */
  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      const flat = [];
      data.categories?.forEach((cat) =>
        cat?.queries?.forEach((q) => flat.push(q)),
      );
      data.queries = flat.filter((q) => q?.isActive);

      data.company.hasCV = !!data.company.hasCV;
      data.company.hasSurvey = !!data.company.hasSurvey;

      await axios.put(`${ADMIN.GET_COMPANY_QUERIES}/${company?.id}`, data);

      // auditoria
      const prev = snapshotRef.current || {};
      const curr = {};
      data.categories?.forEach((c) =>
        c?.queries?.forEach((q) => {
          curr[q.QueryId] = {
            cqpId: q.cqpId ?? null,
            isActive: !!q.isActive,
            price: Number(q.price ?? 0),
          };
        }),
      );
      const payloads = [];
      for (const qid of Object.keys(curr)) {
        const a = prev[qid];
        const b = curr[qid];
        if (!a || !b) continue;
        const changed = Number(a.price) !== Number(b.price);
        if (b.cqpId && b.isActive && a.isActive && changed) {
          payloads.push({
            action: "UPDATE",
            entityName: "CompanyQueryPermission",
            entityId: b.cqpId,
            previousValues: { price: Number(a.price) },
            newValues: { price: Number(b.price) },
            changedFields: ["price"],
            reason: "Atualização de preço via painel",
            source: "admin_panel",
            companyId: company?.id,
          });
        }
      }
      if (payloads.length)
        await Promise.allSettled(
          payloads.map((p) => axios.post(AUDIT.CREATE, p)),
        );

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);

      snapshotRef.current = curr;
      fetchCompanies?.();
      onClose?.(false);
    } catch (err) {
      setErrorState(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ---------- UI ---------- */
  return (
    <FormProvider {...methods}>
      <div className="space-y-4">
        {/* Header */}
        <Card>
          <CardHeader
            title={
              <div>
                <div className="text-base md:text-lg font-bold leading-tight">
                  Permissões de Consulta
                </div>
                <div className="text-xs md:text-sm text-gray-500">
                  Cliente:{" "}
                  <span className="font-medium">
                    {company?.name || company?.socialReason}
                  </span>
                </div>
              </div>
            }
            right={
              <div className="flex items-center gap-2">
                <StatChip label="Ativas" value={totalActive} />
                <StatChip
                  label="Tabela"
                  value={
                    tableId
                      ? (Array.isArray(tables) &&
                          (tables.find((t) => String(t.id) === String(tableId))
                            ?.name ||
                            `#${tableId}`)) ||
                        `#${tableId}`
                      : "Sem tabela"
                  }
                />
                {(() => {
                  const sel = Array.isArray(tables)
                    ? tables.find((t) => String(t.id) === String(tableId))
                    : null;
                  return sel?.vcm ? (
                    <StatChip label="VCM" value={formatToReal(sel.vcm)} />
                  ) : null;
                })()}
              </div>
            }
          />
        </Card>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Modelo de tabela */}
          <Card>
            <CardHeader
              title={
                <div className="flex items-center gap-2">
                  <MdOutlineTableView className="text-emerald-600" />
                  <span>Modelo de tabela</span>
                </div>
              }
              right={
                detachedFromTable && company?.tableId ? (
                  <Button
                    color="light"
                    size="sm"
                    onClick={() => {
                      setValue(
                        "tableId",
                        company.tableId ? String(company.tableId) : "",
                        {
                          shouldDirty: false,
                        },
                      );
                      setDetachedFromTable(false);
                    }}
                  >
                    Voltar à tabela
                  </Button>
                ) : null
              }
            />
            <CardBody>
              <div className="grid grid-cols-1 md:grid-cols-[minmax(0,420px)_1fr] gap-4">
                <div>
                  <Controller
                    control={methods.control}
                    name="tableId"
                    render={({ field }) => (
                      <SelectInput
                        label="Tabela pré-definida"
                        name="tableId"
                        required={false}
                        value={field.value ?? ""}
                        onChange={(e) => field.onChange(e.target.value)}
                      >
                        <option value="">Nenhum selecionado</option>
                        {Array.isArray(tables) &&
                          tables.map((table) => (
                            <option key={table.id} value={String(table.id)}>
                              {table.name} —{" "}
                              {table.vcm ? formatToReal(table.vcm) : "Sem VCM"}
                            </option>
                          ))}
                      </SelectInput>
                    )}
                  />
                  {company?.tableId &&
                    !detachedFromTable &&
                    String(tableId) === String(company.tableId) && (
                      <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-emerald-700 font-medium">
                        <MdOutlineTableView size={13} />
                        Tabela pré-definida vinculada a este cliente
                      </div>
                    )}
                </div>

                <div className="flex items-end">
                  <p className="text-xs text-gray-500">
                    Ao alterar preços/ativação manualmente, o cliente será
                    desvinculado da tabela pré-definida.
                  </p>
                </div>
              </div>

              {detachedFromTable && (
                <Alert
                  color="warning"
                  icon={HiInformationCircle}
                  className="mt-3"
                >
                  Valores alterados manualmente. Este cliente foi desvinculado
                  da tabela pré-definida.
                </Alert>
              )}
            </CardBody>
          </Card>

          {/* ===== Permissão de Negativação (CV) ===== */}
          {isCompany && (
            <Card>
              <CardHeader
                title={
                  <div className="flex items-center gap-2">
                    <span className="text-emerald-700 font-medium">
                      Negativação (CV)
                    </span>
                  </div>
                }
                right={
                  <div className="text-xs text-gray-500">
                    Permissão específica deste cliente
                  </div>
                }
              />
              <CardBody>
                <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_220px] gap-4 items-center">
                  <Controller
                    name="company.hasCV"
                    control={methods.control}
                    render={({ field }) => (
                      <CustomSwitch
                        id="switch-company-hasCV"
                        checked={!!field.value}
                        onChange={(val) => field.onChange(!!val)}
                        label={
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-900">
                              Habilitar negativação para este cliente
                            </span>
                            <span className="text-xs text-gray-500">
                              Concede a permissão para registrar/baixar
                              negativações (CV).
                            </span>
                          </div>
                        }
                        size="md"
                        color="emerald"
                      />
                    )}
                  />

                  <div className="w-full md:w-[220px]">
                    <CurrencyInput
                      name="company.priceCV"
                      label="Preço"
                      control={methods.control}
                      disabled={!methods.watch("company.hasCV")}
                      className={
                        methods.watch("company.hasCV") ? "" : "opacity-60"
                      }
                    />
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Seção de categoria */}
          <Card>
            <CardHeader title="Consultas" />
            <CardBody>
              <div className="flex items-center gap-2 mb-4">
                <label className="text-xs text-gray-500 whitespace-nowrap hidden lg:block">
                  Categoria
                </label>
                <Select
                  value={String(activeCategoryIdx)}
                  onChange={(e) => setActiveCategoryIdx(Number(e.target.value))}
                  className="min-w-[220px]"
                >
                  {(derived || []).map((c, i) => (
                    <option key={i} value={i}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex flex-col gap-3">
                {/* linha 1: segmented + categoria */}
                <div className="flex flex-col md:flex-row gap-3 lg:items-center">
                  <Segmented
                    value={subTab}
                    onChange={(v) => setSubTab(v)}
                    stats={tabStats} // { main: {total, visible}, add: {total, visible} }
                  />

                  {/* filtro + ações */}
                  <div className="flex flex-1 flex-col md:flex-row md:items-center gap-2 md:justify-end">
                    <div className="relative w-full flex-1">
                      <TextInput
                        icon={MdFilterList}
                        placeholder="Filtrar pelo nome…"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="w-full"
                      />
                      {filter?.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setFilter("")}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
                          title="Limpar filtro"
                        >
                          <MdClear size={16} className="text-gray-500" />
                        </button>
                      )}
                    </div>

                    <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                      <Button
                        color="success"
                        size="xs"
                        className="w-full sm:w-auto"
                        onClick={() => {
                          entriesForSubTab.forEach((e) =>
                            setValue(e.fieldPathActive, true, {
                              shouldDirty: true,
                            }),
                          );
                        }}
                      >
                        Selecionar todas
                      </Button>

                      <Button
                        color="failure"
                        size="xs"
                        className="w-full sm:w-auto"
                        onClick={() => {
                          entriesForSubTab.forEach((e) =>
                            setValue(e.fieldPathActive, false, {
                              shouldDirty: true,
                            }),
                          );
                        }}
                      >
                        Limpar todas
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Lista virtualizada */}
              <div className="mt-4">
                {activeCategory ? (
                  entriesForSubTab.length ? (
                    <VirtualQueryList
                      entries={entriesForSubTab}
                      getCqpId={(e) => watch(e.fieldPathCqp)}
                      onOpenHistory={(cqpId, title) =>
                        openHistory(cqpId, title)
                      }
                      onValidityChange={handleValidityChange}
                      containerHeight={isMobile ? 420 : 540}
                    />
                  ) : (
                    <div className="text-sm text-gray-500">
                      Sem itens para mostrar.
                    </div>
                  )
                ) : (
                  <div className="text-sm text-gray-500">
                    Selecione uma categoria.
                  </div>
                )}
              </div>
            </CardBody>
          </Card>

          {/* Rodapé */}
          <Card className="overflow-hidden">
            <CardBody className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="text-xs text-gray-500">
                {tableId ? (
                  <>
                    Seguindo:{" "}
                    <span className="font-medium">
                      {(Array.isArray(tables) &&
                        (tables.find((t) => String(t.id) === String(tableId))
                          ?.name ||
                          `#${tableId}`)) ||
                        `#${tableId}`}
                    </span>
                  </>
                ) : (
                  <>Sem tabela vinculada</>
                )}
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <Button
                  color="light"
                  className="w-full sm:w-auto"
                  onClick={() => onClose?.(false)}
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="primary w-full sm:w-auto"
                  isProcessing={isSubmitting}
                  processingSpinner={
                    <AiOutlineLoading className="h-6 w-6 animate-spin" />
                  }
                  disabled={isSubmitting || hasInvalidPrice}
                  title={
                    hasInvalidPrice
                      ? "Corrija os preços abaixo do custo para habilitar"
                      : undefined
                  }
                >
                  Atualizar permissões
                </Button>
              </div>
            </CardBody>
            {saved && (
              <Alert color="success" className="mt-2">
                Permissões salvas com sucesso!
              </Alert>
            )}
          </Card>

          {error && (
            <Alert color="failure" icon={HiInformationCircle} className="mt-2">
              Erro ao atualizar permissões do cliente
            </Alert>
          )}
        </form>

        {(loadingCQ || loadingTables || fetchingTableQueries) && (
          <DrawerLoading />
        )}

        <AuditHistoryModal
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          cqpId={historyCtx.cqpId}
          title={historyCtx.title}
          companyId={company?.id}
        />
      </div>
    </FormProvider>
  );
};

export default CompanyQueriesFormV2;
